import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";
import login_logo from "../../assets/LOGO_PP.png";

// UI Components
import { Card, CardContent } from "../ui/card";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

import styles from "./LoginForm.module.css";

/* ---------------- Icons ---------------- */
const UserIcon = React.memo(() => (
  <svg className={styles.inputIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
));

const LockIcon = React.memo(() => (
  <svg className={styles.inputIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
));

const KeyIcon = React.memo(() => (
  <svg className={styles.tabIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 18v3c0 .6.4 1 1 1h4v-3h3v-3h2l1.4-1.4a6.5 6.5 0 1 0-4-4Z" /><circle cx="16.5" cy="7.5" r=".5" fill="currentColor"/>
  </svg>
));

const MailIcon = React.memo(() => (
  <svg className={styles.tabIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
));

const EyeIcon = React.memo(() => (
  <svg className={styles.eyeIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
  </svg>
));

const EyeOffIcon = React.memo(() => (
  <svg className={styles.eyeIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" /><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" /><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" /><line x1="2" x2="22" y1="2" y2="22" />
  </svg>
));

/* ---------------- Component ---------------- */
const LoginForm = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  const [formState, setFormState] = useState({
    credentials: { user_name: "", password: "" },
    step: 1,
    error: "",
    isLoading: false,
    roles: [],
    selectedRole: "",
    preAuthToken: null,
  });

  const [activeTab, setActiveTab] = useState("password");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [otpStep, setOtpStep] = useState("request"); // "request" | "verify" | "reset"
  
  // Password Visibility States
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const updateFormState = useCallback((updates) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetForm = useCallback(() => {
    updateFormState({
      credentials: { user_name: "", password: "" },
      step: 1, error: "", isLoading: false, roles: [], selectedRole: "", preAuthToken: null,
    });
    setOtpEmail(""); setOtpCode(""); setNewPassword(""); setOtpStep("request"); setIsForgotPassword(false);
    setShowPassword(false); setShowNewPassword(false);
  }, [updateFormState]);

  // Standard Password Login
  const handlePasswordLogin = useCallback(async (e) => {
    e.preventDefault();
    const { user_name, password } = formState.credentials;
    if (!user_name.trim() || !password.trim()) return updateFormState({ error: "Please fill in all fields." });
    updateFormState({ error: "", isLoading: true });

    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/auth/login`, { user_name: user_name.trim(), password });
      if (data.roles.length === 1) await handleRoleSelection(data.roles[0], data.preAuthToken);
      else updateFormState({ preAuthToken: data.preAuthToken, roles: data.roles, step: 2 });
    } catch (err) {
      updateFormState({ error: err.response?.data?.error || "Login failed. Please check your credentials." });
    } finally {
      updateFormState({ isLoading: false });
    }
  }, [formState, updateFormState]);

  // 1. Request Email OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!otpEmail.trim()) return updateFormState({ error: "Please enter your email/username." });
    updateFormState({ error: "", isLoading: true });

    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/auth/otp/request`, {
        email: otpEmail.trim()
      });

      setOtpStep("verify");
      if (data.demoMode && data.demoCode) {
        updateFormState({ error: `[DEMO MODE] Your OTP code is: ${data.demoCode}` });
      } else {
        updateFormState({ error: "OTP sent successfully to your email." });
      }
    } catch (err) {
      updateFormState({ error: err.response?.data?.error || err.response?.data?.message || "Failed to send OTP." });
    } finally { 
      updateFormState({ isLoading: false }); 
    }
  };

  // 2. Verify Email OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length < 6) return updateFormState({ error: "Please enter the 6-digit code." });
    updateFormState({ error: "", isLoading: true });

    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/auth/otp/verify`, {
        email: otpEmail.trim(),
        code: otpCode.trim()
      });

      if (isForgotPassword) {
        setOtpStep("reset");
        updateFormState({ error: "" });
      } else {
        if (data.roles && data.roles.length === 1) {
          await handleRoleSelection(data.roles[0], data.preAuthToken);
        } else {
          updateFormState({
            preAuthToken: data.preAuthToken,
            roles: data.roles || [],
            step: 2,
            error: ""
          });
        }
      }
    } catch (err) {
      updateFormState({ error: err.response?.data?.error || err.response?.data?.message || "Invalid OTP verification." });
    } finally { 
      updateFormState({ isLoading: false }); 
    }
  };

  // 3. Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword.trim()) return updateFormState({ error: "Please enter a new password." });
    updateFormState({ error: "", isLoading: true });

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/auth/reset-password`, {
        email: otpEmail.trim(),
        code: otpCode.trim(),
        newPassword: newPassword.trim()
      });

      updateFormState({ error: "Password reset successful! Please sign in with your new password." });
      setIsForgotPassword(false); 
      setOtpStep("request");
      setActiveTab("password");
    } catch (err) {
      updateFormState({ error: err.response?.data?.error || err.response?.data?.message || "Failed to reset password." });
    } finally { 
      updateFormState({ isLoading: false }); 
    }
  };

  // Role Selection Handler
  const handleRoleSelection = useCallback(async (role, tokenOverride) => {
    const token = tokenOverride || formState.preAuthToken;
    if (!token) return resetForm();
    updateFormState({ isLoading: true });

    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/auth/authorize-role`, { preAuthToken: token, selectedRole: role });
      auth.login({ user_id: data.user.user_id, user_name: data.user.user_name, role: data.user.role_name, token: data.token });
      const dashboards = { ADMIN: "/admin/admin-dashboard", "BATCH COORDINATOR": "/coordinator/coordinator-dashboard", TEACHER: "/teacher/teacher-dashboard", STUDENT: "/student/student-dashboard", INTERVIEWER: "/interviewer/interviewer-dashboard" };
      navigate(dashboards[data.user.role_name] || "/");
    } catch {
      resetForm();
    } finally { updateFormState({ isLoading: false }); }
  }, [formState.preAuthToken, auth, navigate, updateFormState, resetForm]);

  const roleOptions = useMemo(() => formState.roles.map((role) => (
    <label key={role} className={`${styles.roleCard} ${formState.selectedRole === role ? styles.activeRole : ""}`}>
      <input type="radio" className={styles.hiddenRadio} checked={formState.selectedRole === role} onChange={() => updateFormState({ selectedRole: role })} />
      <span>{role}</span>
    </label>
  )), [formState.roles, formState.selectedRole, updateFormState]);

  return (
    <div className={styles.pageBackground}>
      <Card className={styles.loginCard}>
        <div className={styles.headerSection}>
          <div className={styles.logoCircle}>
            <img src={login_logo} alt="Logo" className={styles.logoImage} />
          </div>
        </div>

        <CardContent className={styles.formSection}>
          {formState.error && (
            <div className={styles.errorBanner}>⚠ {formState.error}</div>
          )}

          {formState.step === 1 && (
            <>
              {!isForgotPassword && (
                <div className={styles.tabContainer}>
                  <button type="button" onClick={() => { setActiveTab("password"); resetForm(); }} className={`${styles.tabButton} ${activeTab === "password" ? styles.activeTab : ""}`}>
                    <KeyIcon /> Password
                  </button>
                  <button type="button" onClick={() => { setActiveTab("email"); resetForm(); }} className={`${styles.tabButton} ${activeTab === "email" ? styles.activeTab : ""}`}>
                    <MailIcon /> Email OTP
                  </button>
                </div>
              )}

              {/* Password Login Tab */}
              {!isForgotPassword && activeTab === "password" && (
                <form onSubmit={handlePasswordLogin} className={styles.formStack}>
                  <div className={styles.inputWrapper}>
                    <Label htmlFor="username" className={styles.label}>Username / Email id</Label>
                    <div className={styles.inputContainer}>
                      <UserIcon />
                      <Input
                        id="username"
                        className={styles.inputField}
                        placeholder="Enter your username"
                        value={formState.credentials.user_name}
                        onChange={(e) => updateFormState({ credentials: { ...formState.credentials, user_name: e.target.value } })}
                        required
                      />
                    </div>
                  </div>

                  <div className={styles.inputWrapper}>
                    <div className={styles.labelRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <Label htmlFor="password" className={styles.label} style={{ marginBottom: 0 }}>Password</Label>
                      <button type="button" className={styles.linkButton} onClick={() => setIsForgotPassword(true)}>Forgot Password?</button>
                    </div>
                    <div className={styles.inputContainer} style={{ position: 'relative' }}>
                      <LockIcon />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        className={styles.inputField}
                        placeholder="Enter your password"
                        value={formState.credentials.password}
                        onChange={(e) => updateFormState({ credentials: { ...formState.credentials, password: e.target.value } })}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={styles.eyeToggleButton}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                    </div>
                  </div>

                  <Button type="submit" className={styles.continueButton} disabled={formState.isLoading}>
                    {formState.isLoading ? <><span className={styles.spinner}></span> Signing in...</> : "Sign in securely >"}
                  </Button>
                </form>
              )}

              {/* Email OTP Login Tab */}
              {!isForgotPassword && activeTab === "email" && (
                <form onSubmit={otpStep === "request" ? handleRequestOTP : handleVerifyOTP} className={styles.formStack}>
                  <div className={styles.inputWrapper}>
                    <Label className={styles.label}>Email Address</Label>
                    <div className={styles.inputContainer}>
                      <MailIcon />
                      <Input
                        type="email"
                        className={styles.inputField}
                        placeholder="you@example.com"
                        value={otpEmail}
                        onChange={(e) => setOtpEmail(e.target.value)}
                        disabled={otpStep !== "request"}
                        required
                      />
                    </div>
                  </div>

                  {otpStep === "verify" && (
                    <div className={styles.inputWrapper}>
                      <Label className={styles.label}>Enter 6-digit Code</Label>
                      <Input
                        type="text"
                        className={styles.inputFieldOtp}
                        placeholder="••••••"
                        maxLength={6}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                        required
                      />
                    </div>
                  )}

                  <Button type="submit" className={styles.continueButton} disabled={formState.isLoading || (otpStep === "verify" && otpCode.length < 6)}>
                    {formState.isLoading ? <><span className={styles.spinner}></span> Processing...</> : (otpStep === "request" ? "Send Email OTP >" : "Verify & Sign in >")}
                  </Button>

                  {otpStep === "verify" && (
                    <button type="button" className={styles.textLink} onClick={() => setOtpStep("request")}>← Use a different email</button>
                  )}
                </form>
              )}

              {/* Forgot Password Flow */}
              {isForgotPassword && (
                <form onSubmit={otpStep === "request" ? handleRequestOTP : otpStep === "verify" ? handleVerifyOTP : handleResetPassword} className={styles.formStack}>
                  <div className={styles.inputWrapper}>
                    <Label className={styles.label}>Registered Username / Email</Label>
                    <div className={styles.inputContainer}>
                      <UserIcon />
                      <Input
                        type="text"
                        className={styles.inputField}
                        placeholder="Enter your username"
                        value={otpEmail}
                        onChange={(e) => setOtpEmail(e.target.value)}
                        disabled={otpStep !== "request"}
                        required
                      />
                    </div>
                  </div>

                  {otpStep === "verify" && (
                    <div className={styles.inputWrapper}>
                      <Label className={styles.label}>Enter OTP Code</Label>
                      <Input
                        type="text"
                        className={styles.inputFieldOtp}
                        placeholder="••••••"
                        maxLength={6}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                        required
                      />
                    </div>
                  )}

                  {otpStep === "reset" && (
                    <div className={styles.inputWrapper}>
                      <Label className={styles.label}>New Password</Label>
                      <div className={styles.inputContainer} style={{ position: 'relative' }}>
                        <LockIcon />
                        <Input
                          type={showNewPassword ? "text" : "password"}
                          className={styles.inputField}
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className={styles.eyeToggleButton}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}
                        >
                          {showNewPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                      </div>
                    </div>
                  )}

                  <Button type="submit" className={styles.continueButton} disabled={formState.isLoading}>
                    {formState.isLoading ? <><span className={styles.spinner}></span> Processing...</> : (otpStep === "request" ? "Send Reset OTP >" : otpStep === "verify" ? "Verify Code >" : "Update Password >")}
                  </Button>

                  <button type="button" className={styles.textLink} onClick={resetForm}>← Back to Login</button>
                </form>
              )}
            </>
          )}

          {/* Role Selection Step */}
          {formState.step === 2 && (
            <div className={styles.formStack}>
              <div className={styles.inputWrapper}>
                <Label className={styles.label}>Select Account Role</Label>
                <div className={styles.roleContainer}>
                  {roleOptions}
                </div>
              </div>

              <Button
                className={styles.continueButton}
                disabled={!formState.selectedRole || formState.isLoading}
                onClick={() => handleRoleSelection(formState.selectedRole)}
              >
                {formState.isLoading ? <><span className={styles.spinner}></span> Logging in...</> : "Login >"}
              </Button>

              <button type="button" className={styles.textLink} onClick={resetForm}>← Back to Login</button>
            </div>
          )}

          <div className={styles.footerText}>
            © 2026 Rajalakshmi Children Foundation. All rights reserved.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginForm;